import SEO from "@/components/SEO";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";

interface LibraryItem {
  id: number;
  product_id: number;
  product_name?: string;
  pdf_url?: string;
}

interface PlaylistFolder {
  id: number;
  name: string;
  share_code: string | null;
  is_shared: boolean;
  groups: PlaylistGroup[];
  expanded: boolean;
  user_id: string;
}

interface PlaylistGroup {
  id: number;
  name: string;
  songs: PlaylistSong[];
  expanded: boolean;
}

interface PlaylistSong {
  id: number;
  song_name: string;
  artist_name?: string;
  song_key?: string;
  bpm?: number;
  source: "purchased" | "uploaded";
  pdf_url?: string;
  notes?: string;
}

interface Collaborator {
  id: number;
  permission: string;
  user_id: string;
  profiles: { nome: string; avatar_url: string | null } | null;
}

export default function MinhaPlaylistPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [library, setLibrary] = useState<LibraryItem[]>([]);
  const [folders, setFolders] = useState<PlaylistFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFolderId, setActiveFolderId] = useState<number | null>(null);
  const [activeGroupId, setActiveGroupId] = useState<number | null>(null);
  const [showAddFolderModal, setShowAddFolderModal] = useState(false);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [showAddSongModal, setShowAddSongModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedSongs, setSelectedSongs] = useState<number[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadArtist, setUploadArtist] = useState("");
  const [uploadKey, setUploadKey] = useState("C");
  const [uploadBpm, setUploadBpm] = useState("");
  const [uploading, setUploading] = useState(false);
  const [shareCodeInput, setShareCodeInput] = useState("");
  const [currentShareCode, setCurrentShareCode] = useState("");
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tons = ["A", "Ab", "B", "Bb", "C", "Db", "D", "Eb", "E", "F", "F#", "G"];
  const groupTypes = ["Jovens", "Irmãs", "Coral", "Louvor", "Geral", "Ensaio", "Outro"];

  useEffect(() => {
    if (!user) {
      navigate("/login?redirect=/minha-playlist");
      return;
    }
    loadData();
  }, [user]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  async function loadData() {
    setLoading(true);
    await Promise.all([loadLibrary(), loadPlaylists()]);
    setLoading(false);
  }

  async function loadLibrary() {
    const { data } = await supabase
      .from("user_library")
      .select("id, product_id, pdf_url, product_items(name)")
      .eq("user_id", user!.id)
      .order("purchased_at", { ascending: false });
    if (data) {
      setLibrary(
        data.map((item: any) => ({
          id: item.id,
          product_id: item.product_id,
          product_name: item.product_items?.name || "Partitura",
          pdf_url: item.pdf_url || "",
        }))
      );
    }
  }

  function generateShareCode(): string {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";
    for (let i = 0; i < 8; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    return code;
  }

  async function loadPlaylists() {
    // Carregar minhas pastas
    const { data: myFolders } = await supabase
      .from("user_playlists")
      .select("id, name, share_code, is_shared, user_id")
      .eq("user_id", user!.id)
      .order("created_at", { ascending: true });

    // Carregar pastas compartilhadas comigo
    const { data: sharedCollabs } = await supabase
      .from("playlist_collaborators")
      .select("playlist_id, permission")
      .eq("user_id", user!.id);

    const sharedFolderIds = (sharedCollabs || []).map((c: any) => c.playlist_id);
    let sharedFolders: any[] = [];
    if (sharedFolderIds.length > 0) {
      const { data: sf } = await supabase
        .from("user_playlists")
        .select("id, name, share_code, is_shared, user_id")
        .in("id", sharedFolderIds)
        .order("created_at", { ascending: true });
      sharedFolders = sf || [];
    }

    const allFolders = [...(myFolders || []), ...sharedFolders];
    const folderList: PlaylistFolder[] = [];

    for (const folder of allFolders) {
      const { data: groups } = await supabase
        .from("playlist_groups")
        .select("id, name")
        .eq("playlist_id", folder.id)
        .order("sort_order", { ascending: true });

      const groupList: PlaylistGroup[] = [];
      for (const g of groups || []) {
        const { data: songs } = await supabase
          .from("playlist_songs")
          .select("id, song_name, artist_name, song_key, bpm, source, pdf_url, notes")
          .eq("group_id", g.id)
          .order("sort_order", { ascending: true });

        groupList.push({
          id: g.id,
          name: g.name,
          expanded: false,
          songs: (songs || []).map((s: any) => ({
            id: s.id,
            song_name: s.song_name,
            artist_name: s.artist_name || "",
            song_key: s.song_key || "",
            bpm: s.bpm || undefined,
            source: s.source || "purchased",
            pdf_url: s.pdf_url || "",
            notes: s.notes || "",
          })),
        });
      }

      folderList.push({
        id: folder.id,
        name: folder.name,
        share_code: folder.share_code,
        is_shared: folder.is_shared,
        groups: groupList,
        expanded: false,
        user_id: folder.user_id,
      });
    }
    setFolders(folderList);
  }

  async function createFolder() {
    if (!newFolderName.trim() || !user) return;
    const code = generateShareCode();
    const { data: folder } = await supabase
      .from("user_playlists")
      .insert({
        user_id: user.id,
        name: newFolderName.trim(),
        share_code: code,
        is_shared: false,
      })
      .select("id")
      .single();

    if (folder) {
      // Criar grupo padrão "Geral"
      await supabase
        .from("playlist_groups")
        .insert({ playlist_id: folder.id, name: "Geral", sort_order: 0 });

      // Adicionar owner como colaborador
      await supabase.from("playlist_collaborators").insert({
        playlist_id: folder.id,
        user_id: user.id,
        permission: "owner",
      });

      setNewFolderName("");
      setShowAddFolderModal(false);
      setToast({ type: "success", msg: "Pasta criada com grupo Geral!" });
      loadPlaylists();
    }
  }

  async function createGroup(folderId: number) {
    if (!newGroupName.trim()) return;
    const { error } = await supabase
      .from("playlist_groups")
      .insert({ playlist_id: folderId, name: newGroupName.trim(), sort_order: 0 });
    if (!error) {
      setNewGroupName("");
      setShowAddGroupModal(false);
      setToast({ type: "success", msg: `Grupo "${newGroupName.trim()}" criado!` });
      loadPlaylists();
    } else {
      setToast({ type: "error", msg: "Erro ao criar grupo." });
    }
  }

  async function addSongsToGroup(groupId: number) {
    if (selectedSongs.length === 0) return;
    const items = library.filter((l) => selectedSongs.includes(l.id));
    const toInsert = items.map((item, idx) => ({
      group_id: groupId,
      song_name: item.product_name || "Partitura",
      artist_name: "",
      song_key: "",
      bpm: null,
      source: "purchased" as const,
      pdf_url: item.pdf_url || "",
      sort_order: idx,
    }));

    const { error } = await supabase.from("playlist_songs").insert(toInsert);
    if (error) {
      setToast({ type: "error", msg: "Erro ao adicionar músicas." });
    } else {
      setToast({ type: "success", msg: "Músicas adicionadas!" });
      setSelectedSongs([]);
      setShowAddSongModal(false);
      loadPlaylists();
    }
  }

  async function handleUploadToGroup(groupId: number) {
    if (!uploadFile || !uploadTitle.trim() || !user) return;
    setUploading(true);
    try {
      const ext = uploadFile.name.split(".").pop() || "pdf";
      const path = `user-uploads/${user.id}/${Date.now()}_${uploadTitle.replace(/\s+/g, "_")}.${ext}`;

      const { error: upError } = await supabase.storage
        .from("user-files")
        .upload(path, uploadFile, { upsert: true });

      if (upError) throw new Error(upError.message);

      const { data: urlData } = supabase.storage.from("user-files").getPublicUrl(path);
      const pdfUrl = urlData.publicUrl;

      const { error } = await supabase.from("playlist_songs").insert({
        group_id: groupId,
        song_name: uploadTitle.trim(),
        artist_name: uploadArtist.trim() || null,
        song_key: uploadKey || null,
        bpm: uploadBpm ? parseInt(uploadBpm) : null,
        source: "uploaded",
        pdf_url: pdfUrl,
        sort_order: 999,
      });

      if (error) throw new Error(error.message);

      setToast({ type: "success", msg: "PDF importado com sucesso!" });
      setUploadFile(null);
      setUploadTitle("");
      setUploadArtist("");
      setUploadKey("C");
      setUploadBpm("");
      setShowUploadModal(false);
      loadPlaylists();
    } catch (err: any) {
      setToast({ type: "error", msg: err.message || "Erro ao importar PDF." });
    } finally {
      setUploading(false);
    }
  }

  async function deleteSong(songId: number) {
    const { error } = await supabase.from("playlist_songs").delete().eq("id", songId);
    if (!error) {
      setToast({ type: "success", msg: "Música removida." });
      loadPlaylists();
    }
  }

  async function deleteGroup(groupId: number) {
    await supabase.from("playlist_songs").delete().eq("group_id", groupId);
    const { error } = await supabase.from("playlist_groups").delete().eq("id", groupId);
    if (!error) {
      setToast({ type: "success", msg: "Grupo removido." });
      loadPlaylists();
    }
  }

  async function deleteFolder(folderId: number) {
    const folder = folders.find((f) => f.id === folderId);
    if (folder?.user_id !== user?.id) {
      setToast({ type: "error", msg: "Você só pode excluir suas próprias pastas." });
      return;
    }
    // Deleta em cascata
    for (const g of folder.groups) {
      await supabase.from("playlist_songs").delete().eq("group_id", g.id);
    }
    await supabase.from("playlist_groups").delete().eq("playlist_id", folderId);
    await supabase.from("playlist_collaborators").delete().eq("playlist_id", folderId);
    const { error } = await supabase.from("user_playlists").delete().eq("id", folderId);
    if (!error) {
      setToast({ type: "success", msg: "Pasta removida." });
      loadPlaylists();
    }
  }

  async function shareFolder(folderId: number) {
    const code = generateShareCode();
    const { error } = await supabase
      .from("user_playlists")
      .update({ share_code: code, is_shared: true })
      .eq("id", folderId);
    if (!error) {
      setCurrentShareCode(code);
      setShowShareModal(true);
      loadPlaylists();
    }
  }

  async function joinByCode() {
    if (!shareCodeInput.trim() || !user) return;
    const { data: folder } = await supabase
      .from("user_playlists")
      .select("id, is_shared")
      .eq("share_code", shareCodeInput.trim().toUpperCase())
      .maybeSingle();

    if (!folder || !folder.is_shared) {
      setToast({ type: "error", msg: "Código inválido ou pasta não está compartilhada." });
      return;
    }

    const { data: existing } = await supabase
      .from("playlist_collaborators")
      .select("id")
      .eq("playlist_id", folder.id)
      .eq("user_id", user.id)
      .maybeSingle();

    if (existing) {
      setToast({ type: "error", msg: "Você já tem acesso a esta pasta." });
      return;
    }

    const { error } = await supabase.from("playlist_collaborators").insert({
      playlist_id: folder.id,
      user_id: user.id,
      permission: "viewer",
    });

    if (!error) {
      setToast({ type: "success", msg: "Você entrou na pasta com sucesso!" });
      setShowJoinModal(false);
      setShareCodeInput("");
      loadPlaylists();
    } else {
      setToast({ type: "error", msg: "Erro ao entrar na pasta." });
    }
  }

  async function loadCollaborators(folderId: number) {
    const { data } = await supabase
      .from("playlist_collaborators")
      .select("id, permission, user_id, profiles(nome, avatar_url)")
      .eq("playlist_id", folderId);
    setCollaborators((data || []) as any);
    setShowCollaboratorsModal(true);
  }

  function toggleSongSelection(id: number) {
    setSelectedSongs((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  }

  function toggleFolder(folderId: number) {
    setFolders((prev) =>
      prev.map((f) => (f.id === folderId ? { ...f, expanded: !f.expanded } : f))
    );
  }

  function toggleGroup(folderId: number, groupId: number) {
    setFolders((prev) =>
      prev.map((f) =>
        f.id === folderId
          ? {
              ...f,
              groups: f.groups.map((g) =>
                g.id === groupId ? { ...g, expanded: !g.expanded } : g
              ),
            }
          : f
      )
    );
  }

  if (!user) return null;

  return (
    <>
      <SEO
        title="Minha Playlist"
        description="Organize seus eventos e cultos em pastas, grupos e músicas. Compartilhe playlists com sua equipe e ministério na GodoyProd."
        keywords="minha playlist, organizar músicas, culto, ministério de louvor, GodoyProd"
        noindex
        canonicalPath="/minha-playlist"
      />
      <div className="min-h-screen bg-godoy-black">
        <Navbar />

        <section className="pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div className="mb-10">
              <span className="text-godoy-gold text-sm font-semibold tracking-widest uppercase">
                Minha Área
              </span>
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-godoy-cream mt-3">
                Minha Playlist
              </h1>
              <p className="text-godoy-lightgray mt-3 max-w-xl">
                Organize seus eventos e cultos em pastas, grupos e músicas.
                Compartilhe com sua equipe e ministério.
              </p>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-godoy-gold/10 flex items-center justify-center overflow-hidden">
                  {profile?.avatar_url ? (
                    <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <i className="ri-user-line text-godoy-gold" />
                  )}
                </div>
                <div>
                  <p className="text-godoy-cream font-medium text-sm">{profile?.nome || "Cliente"}</p>
                  <p className="text-godoy-lightgray text-xs">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Ações */}
            <div className="flex flex-wrap gap-3 mb-8">
              <button
                onClick={() => setShowAddFolderModal(true)}
                className="px-5 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm whitespace-nowrap"
              >
                <i className="ri-folder-add-line mr-1.5" />
                Nova Pasta
              </button>
              <button
                onClick={() => setShowJoinModal(true)}
                className="px-5 py-2.5 bg-godoy-charcoal/60 border border-godoy-gray/30 text-godoy-cream rounded-md hover:border-godoy-gold/40 transition-all text-sm whitespace-nowrap"
              >
                <i className="ri-link mr-1.5" />
                Entrar por Código
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
              </div>
            ) : folders.length === 0 ? (
              <div className="text-center py-16 bg-godoy-charcoal/40 border border-godoy-gray/20 rounded-lg">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-godoy-gold/10 flex items-center justify-center">
                  <i className="ri-play-list-line text-2xl text-godoy-gold" />
                </div>
                <h3 className="font-serif text-xl text-godoy-cream mb-2">Nenhuma pasta ainda</h3>
                <p className="text-godoy-lightgray text-sm max-w-md mx-auto mb-6">
                  Crie sua primeira pasta para organizar eventos, cultos ou ensaios.
                  Depois adicione grupos (Jovens, Coral, Louvor...) e músicas.
                </p>
                <button
                  onClick={() => setShowAddFolderModal(true)}
                  className="px-6 py-3 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm"
                >
                  Criar Primeira Pasta
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {folders.map((folder) => (
                  <div
                    key={folder.id}
                    className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-lg overflow-hidden"
                  >
                    {/* Header da Pasta */}
                    <div className="px-6 py-4 border-b border-godoy-gray/20 flex items-center justify-between flex-wrap gap-3">
                      <button
                        onClick={() => toggleFolder(folder.id)}
                        className="flex items-center gap-3 text-left"
                      >
                        {folder.expanded ? (
                          <i className="ri-folder-open-line text-godoy-gold text-xl" />
                        ) : (
                          <i className="ri-folder-line text-godoy-gold text-xl" />
                        )}
                        <div>
                          <h3 className="font-serif text-lg font-semibold text-godoy-cream">{folder.name}</h3>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className="text-godoy-lightgray/60 text-xs">{folder.groups.length} grupos</span>
                            {folder.is_shared && (
                              <span className="text-xs bg-godoy-gold/10 text-godoy-gold px-1.5 py-0.5 rounded">
                                <i className="ri-share-line mr-0.5" /> Compartilhada
                              </span>
                            )}
                            {folder.user_id !== user?.id && (
                              <span className="text-xs bg-sky-500/10 text-sky-400 px-1.5 py-0.5 rounded">
                                <i className="ri-user-received-line mr-0.5" /> Colaborador
                              </span>
                            )}
                          </div>
                        </div>
                        <i className={`ri-arrow-down-s-line text-godoy-lightgray transition-transform ml-2 ${folder.expanded ? "rotate-180" : ""}`} />
                      </button>

                      <div className="flex items-center gap-2 flex-wrap">
                        {folder.user_id === user?.id && (
                          <>
                            <button
                              onClick={() => {
                                setActiveFolderId(folder.id);
                                setShowAddGroupModal(true);
                              }}
                              className="px-3 py-1.5 bg-godoy-gold/10 border border-godoy-gold/30 text-godoy-gold rounded-md text-xs hover:bg-godoy-gold/20 transition-all whitespace-nowrap"
                            >
                              <i className="ri-add-line mr-1" /> Novo Grupo
                            </button>
                            <button
                              onClick={() => shareFolder(folder.id)}
                              className="px-3 py-1.5 bg-godoy-black/40 text-godoy-lightgray rounded-md text-xs hover:text-godoy-gold transition-all whitespace-nowrap"
                            >
                              <i className="ri-share-line mr-1" /> Compartilhar
                            </button>
                            <button
                              onClick={() => loadCollaborators(folder.id)}
                              className="px-3 py-1.5 bg-godoy-black/40 text-godoy-lightgray rounded-md text-xs hover:text-godoy-gold transition-all whitespace-nowrap"
                            >
                              <i className="ri-team-line mr-1" /> Colaboradores
                            </button>
                            <button
                              onClick={() => {
                                if (confirm("Tem certeza que deseja excluir esta pasta e todo o conteúdo?")) {
                                  deleteFolder(folder.id);
                                }
                              }}
                              className="w-7 h-7 flex items-center justify-center rounded-md bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                              title="Excluir pasta"
                            >
                              <i className="ri-delete-bin-line text-xs" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Conteúdo da Pasta */}
                    {folder.expanded && (
                      <div className="p-4 space-y-4">
                        {folder.groups.length === 0 ? (
                          <p className="text-godoy-lightgray/60 text-sm text-center py-4">
                            Nenhum grupo nesta pasta. Adicione um grupo primeiro.
                          </p>
                        ) : (
                          folder.groups.map((group) => (
                            <div key={group.id} className="bg-godoy-black/30 rounded-lg border border-godoy-gray/10 overflow-hidden">
                              {/* Header do Grupo */}
                              <div className="px-4 py-3 flex items-center justify-between">
                                <button
                                  onClick={() => toggleGroup(folder.id, group.id)}
                                  className="flex items-center gap-2 text-left"
                                >
                                  <i
                                    className={`ri-${group.expanded ? "subtract" : "add"}-line text-godoy-gold`}
                                  />
                                  <span className="text-godoy-cream text-sm font-medium">{group.name}</span>
                                  <span className="text-godoy-lightgray/50 text-xs">({group.songs.length} músicas)</span>
                                </button>
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => {
                                      setActiveGroupId(group.id);
                                      setSelectedSongs([]);
                                      setShowAddSongModal(true);
                                    }}
                                    className="px-2.5 py-1 bg-godoy-gold/10 text-godoy-gold rounded text-xs hover:bg-godoy-gold/20 transition-all whitespace-nowrap"
                                  >
                                    <i className="ri-add-line" /> Música
                                  </button>
                                  <button
                                    onClick={() => {
                                      setActiveGroupId(group.id);
                                      setShowUploadModal(true);
                                    }}
                                    className="px-2.5 py-1 bg-godoy-black/40 text-godoy-lightgray rounded text-xs hover:text-godoy-gold transition-all whitespace-nowrap"
                                  >
                                    <i className="ri-upload-2-line" /> PDF
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (confirm("Excluir este grupo e todas as músicas?")) deleteGroup(group.id);
                                    }}
                                    className="w-6 h-6 flex items-center justify-center rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                                  >
                                    <i className="ri-delete-bin-line text-xs" />
                                  </button>
                                </div>
                              </div>

                              {/* Músicas do Grupo */}
                              {group.expanded && (
                                <div className="divide-y divide-godoy-gray/10">
                                  {group.songs.length === 0 ? (
                                    <p className="px-4 py-3 text-godoy-lightgray/50 text-xs text-center">
                                      Nenhuma música. Adicione partituras compradas ou importe um PDF.
                                    </p>
                                  ) : (
                                    group.songs.map((song, idx) => (
                                      <div
                                        key={song.id}
                                        className="px-4 py-3 flex items-center justify-between hover:bg-godoy-black/20 transition-colors"
                                      >
                                        <div className="flex items-center gap-3">
                                          <span className="text-godoy-lightgray/50 text-xs w-5">{idx + 1}</span>
                                          <div>
                                            <p className="text-godoy-cream text-sm">{song.song_name}</p>
                                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                              {song.artist_name && (
                                                <span className="text-godoy-lightgray/60 text-xs">{song.artist_name}</span>
                                              )}
                                              {song.song_key && (
                                                <span className="text-godoy-gold text-xs bg-godoy-gold/10 px-1.5 py-0.5 rounded">
                                                  {song.song_key}
                                                </span>
                                              )}
                                              {song.bpm && (
                                                <span className="text-godoy-lightgray/50 text-xs">{song.bpm} BPM</span>
                                              )}
                                              <span
                                                className={`text-xs px-1.5 py-0.5 rounded ${
                                                  song.source === "purchased"
                                                    ? "bg-emerald-500/10 text-emerald-400"
                                                    : "bg-amber-500/10 text-amber-400"
                                                }`}
                                              >
                                                {song.source === "purchased" ? "Comprado" : "Importado"}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          {song.pdf_url && (
                                            <a
                                              href={song.pdf_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="w-7 h-7 flex items-center justify-center rounded bg-godoy-gold/10 text-godoy-gold hover:bg-godoy-gold/20 transition-all"
                                              title="Abrir PDF"
                                            >
                                              <i className="ri-eye-line text-xs" />
                                            </a>
                                          )}
                                          <button
                                            onClick={() => deleteSong(song.id)}
                                            className="w-7 h-7 flex items-center justify-center rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                                            title="Remover"
                                          >
                                            <i className="ri-delete-bin-line text-xs" />
                                          </button>
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <Footer />

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-[150] max-w-sm">
            <div
              className={`px-5 py-3 rounded-md text-sm font-medium border ${
                toast.type === "success"
                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                  : "bg-red-500/10 border-red-500/30 text-red-400"
              }`}
            >
              <div className="flex items-center gap-2">
                <i className={toast.type === "success" ? "ri-check-line" : "ri-error-warning-line"} />
                {toast.msg}
                <button onClick={() => setToast(null)} className="ml-2 text-godoy-lightgray hover:text-godoy-cream">
                  <i className="ri-close-line" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Nova Pasta */}
        {showAddFolderModal && (
          <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-godoy-charcoal border border-godoy-gray/30 rounded-xl max-w-md w-full p-6">
              <h3 className="font-serif text-lg font-semibold text-godoy-cream mb-4">Nova Pasta</h3>
              <p className="text-godoy-lightgray text-sm mb-4">Crie uma pasta para organizar um evento, culto ou ensaio.</p>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Ex: Culto Domingo Manhã"
                className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm mb-5"
              />
              <div className="flex gap-3">
                <button
                  onClick={createFolder}
                  disabled={!newFolderName.trim()}
                  className="flex-1 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-50 whitespace-nowrap"
                >
                  Criar Pasta
                </button>
                <button
                  onClick={() => { setShowAddFolderModal(false); setNewFolderName(""); }}
                  className="px-5 py-2.5 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 transition-all text-sm whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Novo Grupo */}
        {showAddGroupModal && activeFolderId && (
          <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-godoy-charcoal border border-godoy-gray/30 rounded-xl max-w-md w-full p-6">
              <h3 className="font-serif text-lg font-semibold text-godoy-cream mb-4">Novo Grupo</h3>
              <p className="text-godoy-lightgray text-sm mb-4">Adicione um grupo à pasta. Ex: Jovens, Coral, Louvor...</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {groupTypes.map((t) => (
                  <button
                    key={t}
                    onClick={() => setNewGroupName(t)}
                    className={`px-3 py-1.5 rounded-md text-xs transition-all ${
                      newGroupName === t
                        ? "bg-godoy-gold/20 text-godoy-gold border border-godoy-gold/40"
                        : "bg-godoy-black/40 text-godoy-lightgray border border-godoy-gray/20 hover:border-godoy-gold/30"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Nome do grupo"
                className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm mb-5"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => createGroup(activeFolderId)}
                  disabled={!newGroupName.trim()}
                  className="flex-1 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-50 whitespace-nowrap"
                >
                  Criar Grupo
                </button>
                <button
                  onClick={() => { setShowAddGroupModal(false); setNewGroupName(""); }}
                  className="px-5 py-2.5 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 transition-all text-sm whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Adicionar Partituras Compradas */}
        {showAddSongModal && activeGroupId && (
          <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-godoy-charcoal border border-godoy-gray/30 rounded-xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto">
              <h3 className="font-serif text-lg font-semibold text-godoy-cream mb-4">Adicionar Partituras</h3>
              <p className="text-godoy-lightgray text-sm mb-4">Selecione partituras que você comprou no site:</p>
              <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
                {library.length === 0 ? (
                  <p className="text-godoy-lightgray/60 text-sm text-center py-4">
                    Você ainda não comprou partituras.
                    <Link to="/biblioteca" className="text-godoy-gold block mt-2 hover:underline">
                      Ir para Biblioteca
                    </Link>
                  </p>
                ) : (
                  library.map((item) => (
                    <label
                      key={item.id}
                      className="flex items-center gap-3 p-3 bg-godoy-black/40 rounded-md cursor-pointer hover:bg-godoy-black/60 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSongs.includes(item.id)}
                        onChange={() => toggleSongSelection(item.id)}
                        className="w-4 h-4 accent-godoy-gold"
                      />
                      <div className="flex-1">
                        <p className="text-godoy-cream text-sm">{item.product_name}</p>
                        <p className="text-godoy-lightgray/60 text-xs">Comprado</p>
                      </div>
                      {item.pdf_url && (
                        <a
                          href={item.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-godoy-gold text-xs hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Ver
                        </a>
                      )}
                    </label>
                  ))
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => addSongsToGroup(activeGroupId)}
                  disabled={selectedSongs.length === 0}
                  className="flex-1 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-50 whitespace-nowrap"
                >
                  Adicionar {selectedSongs.length > 0 ? `(${selectedSongs.length})` : ""}
                </button>
                <button
                  onClick={() => { setShowAddSongModal(false); setSelectedSongs([]); }}
                  className="px-5 py-2.5 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 transition-all text-sm whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Importar PDF */}
        {showUploadModal && activeGroupId && (
          <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-godoy-charcoal border border-godoy-gray/30 rounded-xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto">
              <h3 className="font-serif text-lg font-semibold text-godoy-cream mb-4">Importar PDF do Dispositivo</h3>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm text-godoy-lightgray mb-1.5">Título da música</label>
                  <input
                    type="text"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    placeholder="Ex: Hosana"
                    className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-godoy-lightgray mb-1.5">Artista (opcional)</label>
                  <input
                    type="text"
                    value={uploadArtist}
                    onChange={(e) => setUploadArtist(e.target.value)}
                    placeholder="Ex: Fernandinho"
                    className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-godoy-lightgray mb-1.5">Tom</label>
                    <select
                      value={uploadKey}
                      onChange={(e) => setUploadKey(e.target.value)}
                      className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream focus:outline-none focus:border-godoy-gold/50 text-sm"
                    >
                      {tons.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-godoy-lightgray mb-1.5">BPM (opcional)</label>
                    <input
                      type="number"
                      value={uploadBpm}
                      onChange={(e) => setUploadBpm(e.target.value)}
                      placeholder="120"
                      className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-godoy-lightgray mb-1.5">Arquivo PDF</label>
                  <div
                    className="border-2 border-dashed border-godoy-gray/30 rounded-md p-6 text-center cursor-pointer hover:border-godoy-gold/50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    />
                    <i className="ri-file-upload-line text-2xl text-godoy-gold mb-2" />
                    <p className="text-godoy-cream text-sm">{uploadFile ? uploadFile.name : "Clique para selecionar um PDF"}</p>
                    <p className="text-godoy-lightgray/60 text-xs mt-1">Máximo 10MB</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => handleUploadToGroup(activeGroupId)}
                  disabled={!uploadFile || !uploadTitle.trim() || uploading}
                  className="flex-1 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-50 whitespace-nowrap"
                >
                  {uploading ? "Importando..." : "Importar"}
                </button>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFile(null);
                    setUploadTitle("");
                    setUploadArtist("");
                    setUploadKey("C");
                    setUploadBpm("");
                  }}
                  className="px-5 py-2.5 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 transition-all text-sm whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Compartilhar */}
        {showShareModal && (
          <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-godoy-charcoal border border-godoy-gray/30 rounded-xl max-w-sm w-full p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-godoy-gold/10 flex items-center justify-center">
                <i className="ri-share-line text-2xl text-godoy-gold" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-godoy-cream mb-2">Compartilhar Pasta</h3>
              <p className="text-godoy-lightgray text-sm mb-4">
                Envie este código para seus colaboradores entrarem nesta pasta.
              </p>
              <div className="bg-godoy-black/60 border border-godoy-gold/30 rounded-lg p-4 mb-5">
                <p className="font-mono text-2xl text-godoy-gold tracking-widest">{currentShareCode}</p>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(currentShareCode);
                  setToast({ type: "success", msg: "Código copiado!" });
                }}
                className="w-full py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm mb-3 whitespace-nowrap"
              >
                <i className="ri-clipboard-line mr-1.5" /> Copiar Código
              </button>
              <button
                onClick={() => setShowShareModal(false)}
                className="w-full py-2.5 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 transition-all text-sm whitespace-nowrap"
              >
                Fechar
              </button>
            </div>
          </div>
        )}

        {/* Modal: Entrar por Código */}
        {showJoinModal && (
          <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-godoy-charcoal border border-godoy-gray/30 rounded-xl max-w-sm w-full p-6">
              <h3 className="font-serif text-lg font-semibold text-godoy-cream mb-2">Entrar em uma Pasta</h3>
              <p className="text-godoy-lightgray text-sm mb-4">Digite o código de compartilhamento recebido:</p>
              <input
                type="text"
                value={shareCodeInput}
                onChange={(e) => setShareCodeInput(e.target.value.toUpperCase())}
                placeholder="ABCD1234"
                className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm mb-5 uppercase tracking-widest"
              />
              <div className="flex gap-3">
                <button
                  onClick={joinByCode}
                  disabled={shareCodeInput.trim().length < 4}
                  className="flex-1 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-50 whitespace-nowrap"
                >
                  Entrar
                </button>
                <button
                  onClick={() => { setShowJoinModal(false); setShareCodeInput(""); }}
                  className="px-5 py-2.5 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 transition-all text-sm whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Colaboradores */}
        {showCollaboratorsModal && (
          <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-godoy-charcoal border border-godoy-gray/30 rounded-xl max-w-sm w-full p-6">
              <h3 className="font-serif text-lg font-semibold text-godoy-cream mb-4">Colaboradores</h3>
              <div className="space-y-3 mb-5">
                {collaborators.length === 0 ? (
                  <p className="text-godoy-lightgray/60 text-sm text-center py-2">Nenhum colaborador ainda.</p>
                ) : (
                  collaborators.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 p-2 bg-godoy-black/40 rounded-md">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-godoy-charcoal flex-shrink-0">
                        {c.profiles?.avatar_url ? (
                          <img src={c.profiles.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <i className="ri-user-line text-godoy-lightgray w-full h-full flex items-center justify-center" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-godoy-cream text-sm truncate">{c.profiles?.nome || "Usuário"}</p>
                        <p className="text-godoy-lightgray/50 text-xs capitalize">{c.permission}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <button
                onClick={() => setShowCollaboratorsModal(false)}
                className="w-full py-2.5 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 transition-all text-sm whitespace-nowrap"
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}